import type { TaggedImage } from '@watchkeeper/shared-types';

import type { ScrapedWatchEntry } from './BulkImport';
import { stripInlineMarkup } from '../../services/stripInlineMarkup';
import { woocommerceProductDocId, wooHost } from './storeDocIds';
import { storeDescriptionText } from './storeText';

/**
 * WooCommerce store import — the sibling of ShopifyScraper and
 * SquareOnlineScraper.
 *
 * Reads the shop's public **Store API** (`/wp-json/wc/store/v1/`), which every
 * current WooCommerce install exposes unauthenticated. It sends no
 * `Access-Control-Allow-Origin`, so every call goes through the caller's
 * server-side text fetch (the `fetchHtml` callable) rather than the browser.
 *
 * Flow: the pasted URL's origin → `/products/categories` (which also proves
 * the site is WooCommerce) → if the URL is one of the categories' `permalink`s
 * (independent of the shop's category base) import that category, otherwise —
 * home page, `/shop/` — import the WHOLE shop → page `/products` 100 at a time
 * until a short page.
 *
 * Scope: whole shop (the common case — a single-brand shop) or one category,
 * either way under one brand, one reference per product. Variable products are flagged (`storeOptionCount`) as
 * Phase 2 variant candidates rather than split. The Store API only ever lists
 * published, catalogue-visible products; password-protected ones are skipped.
 *
 * Recon: scratchpad/woocommerce/MEMO.md (Alexander Dauvin + Bryan Laurent).
 */

export const WOO_PAGE_SIZE = 100;
const MAX_PAGES = 100;
const STORE_API = 'wc/store/v1';

/**
 * Fetches a URL server-side and returns the body text; throws on failure. An
 * error carrying a numeric `status` (FetchHtmlError) is reported with it.
 */
export type FetchText = (url: string) => Promise<string>;

/**
 * Where a shop's Store API answers. Most sites use pretty permalinks
 * (`/wp-json/…`); a site without them still answers on `?rest_route=`.
 */
export interface WooApi {
  origin: string;
  mode: 'wp-json' | 'rest_route';
}

export function wooApiUrl(
  api: WooApi,
  path: string,
  params: Record<string, string | number> = {},
): string {
  const qs = new URLSearchParams(
    Object.entries(params).map(([k, v]) => [k, String(v)]),
  ).toString();
  if (api.mode === 'rest_route') {
    return `${api.origin}/?rest_route=/${STORE_API}/${path}${qs ? `&${qs}` : ''}`;
  }
  return `${api.origin}/wp-json/${STORE_API}/${path}${qs ? `?${qs}` : ''}`;
}

export interface WooCategory {
  id: number;
  name: string;
  slug: string;
  parent: number;
  count: number;
  permalink: string;
}

export interface WooImage {
  src?: string;
  alt?: string;
}

export interface WooProduct {
  id: number;
  name: string;
  sku?: string;
  type?: string;
  permalink?: string;
  description?: string;
  short_description?: string;
  prices?: {
    price?: string;
    regular_price?: string;
    currency_code?: string;
    currency_minor_unit?: number;
  };
  images?: WooImage[];
  variations?: Array<{ id: number; attributes?: Array<{ name?: string; value?: string }> }>;
  /** The product categories it is filed in (Store API: id + name, and more). */
  categories?: Array<{ id: number; name?: string }>;
  /** On a variation (type 'variation'): its parent product's id. */
  parent?: number;
  /** On a variation: "Select an opal: Azure Opal" (attribute: value, …). */
  variation?: string;
  is_in_stock?: boolean;
  is_password_protected?: boolean;
}

const NOT_WOO =
  'This site does not answer as a WooCommerce store (no public Store API at /wp-json/wc/store/v1). Check the link is a WooCommerce shop.';

function statusOf(err: unknown): number | undefined {
  const status = (err as { status?: unknown } | null)?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Why the Store API could not be read when no attempt got an answer at all —
 * kept apart from NOT_WOO, which used to swallow these: a shop whose host
 * 403'd our request was reported as "not a WooCommerce store".
 */
function unreachableMessage(err: unknown): string {
  // The fetch ladder already tried every request shape and wrote the verdict
  // (StoreApiBlockedError) — pass it through.
  if ((err as { blockedByHost?: unknown } | null)?.blockedByHost === true) {
    return (err as Error).message;
  }
  const status = statusOf(err);
  if (status === 404) return NOT_WOO;
  if (status) {
    return `The shop refused the request for its product catalogue (HTTP ${status}). Its host may block automated access — try again later, or tell the dev team which shop it is.`;
  }
  const detail = err instanceof Error && err.message ? ` (${err.message})` : '';
  return `Could not reach the shop's product catalogue${detail}.`;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/**
 * The shop's categories, trying `/wp-json/` then `?rest_route=`. The first
 * answer that is a JSON array wins and fixes the API mode for the rest of the
 * run.
 *
 * Two different failures, reported differently:
 *  - the site ANSWERED, but with something that is not the Store API (HTML from
 *    a non-WordPress site, a WP error object) → "not a WooCommerce store";
 *  - NO attempt got an answer (refused with a status, timed out, unreachable)
 *    → say so, with the status. A 404 on every attempt is the one refusal that
 *    does mean "no Store API here".
 */
/** The `<title>` of an HTML answer, for saying what the site sent instead of JSON. */
function pageTitle(text: string): string | null {
  const m = /<title[^>]*>([^<]{1,120})<\/title>/i.exec(text);
  return m ? m[1].replace(/\s+/g, ' ').trim() || null : null;
}

export async function fetchWooCategories(
  origin: string,
  fetchText: FetchText,
): Promise<{ api: WooApi; categories: WooCategory[] }> {
  let answeredWithoutApi = false;
  /** An HTML page where the API should be — a non-WC site, or a block we don't recognise. */
  let htmlAnswer: string | null = null;
  let firstError: unknown;
  for (const mode of ['wp-json', 'rest_route'] as const) {
    const api: WooApi = { origin, mode };
    let text: string;
    try {
      text = await fetchText(wooApiUrl(api, 'products/categories'));
    } catch (err) {
      firstError ??= err;
      continue;
    }
    const body = parseJson(text);
    if (Array.isArray(body)) return { api, categories: body as WooCategory[] };
    answeredWithoutApi = true;
    if (body === undefined && /<html|<!doctype/i.test(text)) htmlAnswer ??= text;
  }
  if (htmlAnswer !== null) {
    // The site answered the Store API address with a web page. Either it isn't
    // WooCommerce, or it is and is blocking us with a page we don't recognise
    // as a challenge. Say what it sent, and let the curator paste the JSON if
    // they know it's a WooCommerce shop.
    const title = pageTitle(htmlAnswer);
    throw Object.assign(
      new Error(
        `This site answered the WooCommerce Store API address with a web page${title ? ` titled “${title}”` : ''} instead of product data. ` +
          'Either it is not a WooCommerce shop, or it is blocking automated requests — if you know it is a WooCommerce shop, paste the JSON instead.',
      ),
      { answeredHtml: true },
    );
  }
  if (answeredWithoutApi || firstError === undefined) throw new Error(NOT_WOO);
  // Keep the status and the "blocked" flag on what we throw: the caller offers
  // the paste-the-JSON fallback for a refused or unreachable shop, but not for
  // "this isn't WooCommerce".
  throw Object.assign(new Error(unreachableMessage(firstError)), {
    status: statusOf(firstError) ?? 0,
    blockedByHost: (firstError as { blockedByHost?: unknown } | null)?.blockedByHost === true,
  });
}

/**
 * True for a failure the manual "paste the JSON" fallback can get round: the
 * shop refused us or could not be reached. Not for a 404 ("not WooCommerce"),
 * a bad URL or an unknown category — pasting would not help those.
 */
export function canPasteInstead(err: unknown): boolean {
  if ((err as { blockedByHost?: unknown } | null)?.blockedByHost === true) return true;
  // An HTML page where the Store API should be may be an unrecognised block.
  if ((err as { answeredHtml?: unknown } | null)?.answeredHtml === true) return true;
  const status = statusOf(err);
  return status !== undefined && status !== 404 && (status === 0 || status >= 401);
}

/** Host without `www.`, path without trailing slash, no query or hash. */
export function normalizeWooUrl(url: string): string | null {
  try {
    const u = new URL(url.trim());
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    const path = decodeURIComponent(u.pathname).replace(/\/+$/, '').toLowerCase();
    return `${host}${path}`;
  } catch {
    return null;
  }
}

/** What a pasted URL imports: one category, or the whole shop. */
export type WooScope =
  | { scope: 'category'; category: WooCategory }
  | { scope: 'shop'; category?: undefined };

/**
 * Decide what a pasted URL imports.
 *
 *  - It is a category's public permalink → that category.
 *  - It looks like a category or product page but matches nothing
 *    (`/product-category/…`, `/product/…`) → refused: a typo or a stale link
 *    must not silently turn into "import everything".
 *  - Anything else on the shop (home page, `/shop/`) → the whole shop.
 */
export function resolveWooScope(url: string, categories: WooCategory[]): WooScope | { error: string } {
  const wanted = normalizeWooUrl(url);
  if (!wanted) return { error: 'That is not a valid URL.' };
  const match = categories.find((c) => normalizeWooUrl(c.permalink) === wanted);
  if (match) return { scope: 'category', category: match };
  if (/\/product-category\//.test(wanted)) {
    return {
      error:
        'That URL is not one of this shop’s product categories. Open the category on the shop and copy its address, or paste the shop’s home page to import everything.',
    };
  }
  if (/\/product\//.test(wanted)) {
    return {
      error:
        'That is a product page. Paste the shop’s home page to import everything, or a category address for one category.',
    };
  }
  return { scope: 'shop' };
}

/**
 * Every product in one category (sub-categories included — that is how the
 * Store API's `category` filter behaves), or the whole shop when `categoryId`
 * is undefined; deduped by id. Pages until a page comes back short: the totals
 * live in response headers, which a server-side text fetch does not return.
 */
export async function fetchWooProducts(
  api: WooApi,
  categoryId: number | undefined,
  options: { fetchText: FetchText; onPage?: (page: number) => void },
): Promise<WooProduct[]> {
  const byId = new Map<number, WooProduct>();
  for (let page = 1; page <= MAX_PAGES; page++) {
    const text = await options.fetchText(
      wooApiUrl(api, 'products', {
        ...(categoryId !== undefined ? { category: categoryId } : {}),
        per_page: WOO_PAGE_SIZE,
        page,
      }),
    );
    const body = parseJson(text);
    if (!Array.isArray(body)) {
      throw new Error(`The shop returned something other than a product list on page ${page}.`);
    }
    for (const p of body as WooProduct[]) {
      if (p && typeof p.id === 'number' && !p.is_password_protected) byId.set(p.id, p);
    }
    options.onPage?.(page);
    if (body.length < WOO_PAGE_SIZE) break;
  }
  return [...byId.values()];
}

/** `"47400"` with minor unit 2 → 474. Undefined when absent or not positive. */
export function wooPrice(prices: WooProduct['prices']): number | undefined {
  const raw = prices?.price ?? prices?.regular_price;
  if (raw === undefined || raw === '') return undefined;
  const minor = Number(prices?.currency_minor_unit ?? 2);
  const value = Number(raw) / 10 ** (Number.isFinite(minor) ? minor : 2);
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

export interface WooEntry extends ScrapedWatchEntry {
  wooHost: string;
  wooProductId: string;
}

/**
 * One Bulk Import entry per product. `reference` is the product SKU when it
 * has one (a variable product's parent often does not), else the decoded name;
 * the doc id, not this string, is the identity.
 */
export function wooProductToEntry(product: WooProduct, host: string): WooEntry {
  const name = stripInlineMarkup(product.name) || String(product.id);
  const sku = stripInlineMarkup(product.sku ?? '');
  const images = (product.images ?? [])
    .map((img) => img.src?.trim())
    .filter((u): u is string => !!u);
  const taggedImages: TaggedImage[] = images.map((url) => ({
    url,
    tags: [],
    autoTagged: false,
  }));
  const optionCount = product.type === 'variable' ? product.variations?.length ?? 0 : 0;
  const price = wooPrice(product.prices);
  const storeCategories = (product.categories ?? [])
    .filter((c) => c && c.id !== undefined && c.id !== null)
    .map((c) => ({ id: String(c.id), name: stripInlineMarkup(c.name ?? '') || `Category ${c.id}` }));

  return {
    reference: sku || name,
    model: name,
    title: name,
    productName: name,
    url: product.permalink,
    imgSrc: images[0],
    ...(taggedImages.length > 1 ? { taggedImages } : {}),
    description:
      storeDescriptionText(product.description) ?? storeDescriptionText(product.short_description),
    ...(price !== undefined ? { price } : {}),
    ...(optionCount > 1 ? { storeOptionCount: optionCount } : {}),
    ...(storeCategories.length > 0 ? { storeCategories } : {}),
    wooHost: host,
    wooProductId: String(product.id),
  };
}

// ── Phase 2: variants ───────────────────────────────────────────────────────

/** The Store API page listing every variation in the shop (100 per page). */
export function wooVariationsUrl(api: WooApi, page: number): string {
  return wooApiUrl(api, 'products', { type: 'variation', per_page: WOO_PAGE_SIZE, page });
}

/**
 * Every variation in the shop, grouped by parent product id — one paged list
 * (`type=variation`) rather than a request per variation. Variations carry no
 * categories, so the whole shop's are read and the caller keeps the ones whose
 * parent it has.
 */
export async function fetchWooVariations(
  api: WooApi,
  options: { fetchText: FetchText; onPage?: (page: number) => void },
): Promise<Map<number, WooProduct[]>> {
  const byParent = new Map<number, WooProduct[]>();
  for (let page = 1; page <= MAX_PAGES; page++) {
    const body = parseJson(await options.fetchText(wooVariationsUrl(api, page)));
    if (!Array.isArray(body)) {
      throw new Error(`The shop returned something other than a variation list on page ${page}.`);
    }
    for (const v of body as WooProduct[]) {
      if (v && typeof v.id === 'number' && typeof v.parent === 'number') {
        byParent.set(v.parent, [...(byParent.get(v.parent) ?? []), v]);
      }
    }
    options.onPage?.(page);
    if (body.length < WOO_PAGE_SIZE) break;
  }
  return byParent;
}

/**
 * A variation's own label: "Select an opal: Azure Opal" → "Azure Opal";
 * several attributes join with " / ". Falls back to the parent's attribute
 * slugs for that variation, then to the variation id.
 */
export function wooVariationLabel(variation: WooProduct, parent?: WooProduct): string {
  const fromText = (variation.variation ?? '')
    .split(/,\s*(?=[^,:]+:)/)
    .map((part) => stripInlineMarkup(part.includes(':') ? part.slice(part.indexOf(':') + 1) : part))
    .filter(Boolean)
    .join(' / ');
  if (fromText) return fromText;
  const attrs = parent?.variations?.find((v) => v.id === variation.id)?.attributes ?? [];
  const fromSlugs = attrs
    .map((a) => (a.value ?? '').replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()).trim())
    .filter(Boolean)
    .join(' / ');
  return fromSlugs || `Option ${variation.id}`;
}

/**
 * One Bulk Import entry for one variation of a variable product. Shared
 * fields (description, base images, name) come from the parent product; the
 * variation contributes its SKU, price, image and label. `storeParent` tells
 * the importer what the parent reference is (the product's own SKU or name
 * and images).
 */
export function wooVariationToEntry(
  parent: WooProduct,
  variation: WooProduct,
  host: string,
  variantCount: number,
): WooEntry {
  const base = wooProductToEntry(parent, host);
  const label = wooVariationLabel(variation, parent);
  const sku = stripInlineMarkup(variation.sku ?? '');
  const ownImage = (variation.images ?? []).map((i) => i.src?.trim()).find((u): u is string => !!u);
  const price = wooPrice(variation.prices) ?? base.price;
  const parentImages = base.taggedImages ?? (base.imgSrc ? [{ url: base.imgSrc, tags: [], autoTagged: false }] : []);
  const images = ownImage
    ? [{ url: ownImage, tags: [], autoTagged: false }, ...parentImages.filter((t) => t.url !== ownImage)]
    : parentImages;
  return {
    ...base,
    reference: sku || `${base.model} — ${label}`,
    title: `${base.model} — ${label}`,
    variant: label,
    imgSrc: ownImage ?? base.imgSrc,
    ...(images.length > 1 ? { taggedImages: images } : {}),
    ...(price !== undefined ? { price } : {}),
    // Expanded: the row IS one option, so it is no longer an "options" candidate.
    storeOptionCount: undefined,
    wooVariationId: String(variation.id),
    storeVariantCount: variantCount,
    storeParent: {
      reference: stripInlineMarkup(parent.sku ?? '') || base.model,
      imgSrc: base.imgSrc,
      taggedImages: base.taggedImages,
    },
  };
}

/**
 * Products (+ their variations when expanding) → Bulk Import entries. Shared
 * by the automatic fetch and the paste-JSON fallback so both expand alike. A
 * variable product with fewer than two known variations, or one whose
 * variations were not supplied, stays a single (flagged) row.
 */
export function wooProductsToEntries(
  products: WooProduct[],
  host: string,
  variationsByParent: Map<number, WooProduct[]> | null,
): WooEntry[] {
  return products.flatMap((p) => {
    const variations =
      p.type === 'variable' && variationsByParent
        ? (variationsByParent.get(p.id) ?? []).filter(
            (v) => !p.variations?.length || p.variations.some((pv) => pv.id === v.id),
          )
        : [];
    if (variations.length < 2) return [wooProductToEntry(p, host)];
    // Some shops give every variation the PARENT's SKU (Bryan Laurent's Iris:
    // five × "IRIS-V3"). The doc ids are distinct regardless, but five
    // references all reading "IRIS-V3" are useless to a curator — a SKU that
    // is the parent's, or is shared with a sibling, makes way for
    // "{parent} — {option}".
    const parentSku = stripInlineMarkup(p.sku ?? '');
    const skuCounts = new Map<string, number>();
    for (const v of variations) {
      const sku = stripInlineMarkup(v.sku ?? '');
      if (sku) skuCounts.set(sku, (skuCounts.get(sku) ?? 0) + 1);
    }
    return variations.map((v) => {
      const entry = wooVariationToEntry(p, v, host, variations.length);
      const sku = stripInlineMarkup(v.sku ?? '');
      if (sku && (sku === parentSku || (skuCounts.get(sku) ?? 0) > 1)) {
        return { ...entry, reference: `${entry.storeParent?.reference ?? entry.model} — ${entry.variant}` };
      }
      return entry;
    });
  });
}

export type LoadedWooSource = WooScope & {
  host: string;
  products: WooProduct[];
  entries: WooEntry[];
  /** The shop's whole category list (with parents) — labels the shop-URL category picker. */
  categories: WooCategory[];
  /** Variable products with 2+ variations — each imported as one reference. */
  multiOptionCount: number;
  /** Variable products expanded into variant rows (Phase 2). */
  expandedProductCount: number;
  currency?: string;
};

/** Pasted shop or category URL → scope, products and Bulk Import entries. */
export async function loadWooSource(
  sourceUrl: string,
  deps: { fetchText: FetchText; onPage?: (page: number) => void; expandVariants?: boolean },
): Promise<LoadedWooSource> {
  const url = sourceUrl.trim();
  if (!/^https:\/\//i.test(url)) throw new Error('Enter the shop or category URL, starting https://');
  const origin = new URL(url).origin;
  const { api, categories } = await fetchWooCategories(origin, deps.fetchText);
  const scope = resolveWooScope(url, categories);
  if ('error' in scope) throw new Error(scope.error);
  const products = await fetchWooProducts(api, scope.category?.id, deps);
  const host = wooHost(url);
  const wantsVariants =
    deps.expandVariants !== false && products.some((p) => p.type === 'variable' && (p.variations?.length ?? 0) > 1);
  const variations = wantsVariants ? await fetchWooVariations(api, deps) : null;
  const entries = wooProductsToEntries(products, host, variations);
  return {
    ...scope,
    host,
    products,
    entries,
    categories,
    multiOptionCount: entries.filter((e) => (e.storeOptionCount ?? 0) > 1).length,
    expandedProductCount: new Set(entries.filter((e) => e.wooVariationId).map((e) => e.wooProductId)).size,
    currency: products.find((p) => p.prices?.currency_code)?.prices?.currency_code,
  };
}

/** Re-exported so callers and tests share one id convention. */
export { woocommerceProductDocId };
